import { css } from 'aphrodite';
import { ReactNode } from 'react';
import { Button } from '../../../../web/components-v2/Button';
import { ModuleTag } from '../../../components/accountDetails/ModuleTag';
import { overviewCardStyles } from './overviewCardStyles';

export interface IOverviewCardProps {
	/** Section label shown in the ModuleTag header. */
	title: string;
	/** Accent color for the ModuleTag. */
	color: string;
	/** Whether this card is editable at all (e.g. the admin holds `ModifyAccount`). */
	canEdit: boolean;
	/** Whether the card is currently showing its editable form. */
	isEditMode: boolean;
	/** Whether a save is in flight; disables and spins the Save button. */
	isSaving: boolean;
	/** Enter edit mode. */
	onEdit: () => void;
	/** Commit the current edits. */
	onSave: () => void;
	/** Abandon the current edits. */
	onCancel: () => void;
	/** Optional content rendered inline with the title (e.g. status chips), left of the edit actions. */
	headerAccessory?: ReactNode;
	children: ReactNode;
}

/**
 * Presentational shell for an Account Overview card: a ModuleTag header with an
 * Edit affordance when the card is editable and idle, swapped for a Save/Cancel
 * action bar while editing. All chrome uses components-v2 `Button`; the card body
 * (`children`) is owned by the caller.
 */
export const OverviewCard = ({
	title,
	color,
	canEdit,
	isEditMode,
	isSaving,
	onEdit,
	onSave,
	onCancel,
	headerAccessory,
	children,
}: IOverviewCardProps) => {
	return (
		<section className={css(overviewCardStyles.card)}>
			<header className={css(overviewCardStyles.header)}>
				<div className={css(overviewCardStyles.titleGroup)}>
					<ModuleTag text={title} color={color} />
					{headerAccessory ? <div className={css(overviewCardStyles.headerAccessory)}>{headerAccessory}</div> : null}
				</div>
				{canEdit && !isEditMode && (
					<Button variant='secondary' size='sm' onClick={onEdit}>
						Edit
					</Button>
				)}
				{isEditMode && (
					<div className={css(overviewCardStyles.actionBar)}>
						<Button variant='secondary' size='sm' onClick={onCancel} disabled={isSaving}>
							Cancel
						</Button>
						<Button variant='primary' size='sm' onClick={onSave} isLoading={isSaving}>
							Save
						</Button>
					</div>
				)}
			</header>
			<div className={css(overviewCardStyles.body)}>{children}</div>
		</section>
	);
};
